import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './BestPracticesPanel.module.css';
import TemplateController, {
  TEMPLATE_LOCAL_STORAGE_KEY,
  type TemplateControllerProps,
} from 'components/page_templates/TemplateController';
import classNames from 'classnames';
import { type NoteTemplate, Page } from 'components/framework/models';
import {
  getLocalStoragePreference,
  removeLocalStoragePreference,
} from 'javascripts/utils';
import AhaAnalytics from 'javascripts/analytics';
import { humanize } from 'inflected';
import { type SidePanelItem } from 'javascripts/shared-types';
import { type GlobalNoteTemplate } from 'components/page_templates/types';

export const BEST_PRACTICES_PANEL_KEY = 'best-practices';

type TabberProps = {
  options: string[];
  selected: string;
  onSelect: (option: string) => void;
};

const Tabber: React.FC<TabberProps> = ({ options, selected, onSelect }) => {
  return (
    <div className={styles.tabControls}>
      <i
        className={classNames('far fa-chevron-left', styles.control)}
        onClick={() => {
          const index = options.indexOf(selected);
          if (index > 0) {
            onSelect(options[index - 1]);
          } else {
            onSelect(options[options.length - 1]);
          }
        }}
      />

      {options.map(option => (
        <div
          key={option}
          className={classNames({
            [styles.active]: option === selected,
          })}
          onClick={() => onSelect(option)}
        >
          <i className='fas fa-circle' />
        </div>
      ))}

      <i
        className={classNames('far fa-chevron-right', styles.control)}
        onClick={() => {
          const index = options.indexOf(selected);
          if (index < options.length - 1) {
            onSelect(options[index + 1]);
          } else {
            onSelect(options[0]);
          }
        }}
      />
    </div>
  );
};

type TemplateExampleButtonProps = {
  template: NoteTemplate | GlobalNoteTemplate;
  insertExampleContent: (content: Object) => void;
};

const TemplateExampleButton: React.FC<TemplateExampleButtonProps> = ({
  template,
  insertExampleContent,
}) => {
  const exampleContent =
    template.documentType === Page.DOCUMENT_TYPE_NOTE
      ? template.exampleDescription
      : template.exampleWhiteboard;

  const insertExample = useCallback(() => {
    AhaAnalytics.track(
      'Feature-7285827173424833246',
      'User adds example template',
      {
        'Template type': humanize(template.documentType),
        'Template name': template.name,
      }
    );
    const content = (exampleContent as any).editor_content;
    insertExampleContent(content);
  }, [
    exampleContent,
    insertExampleContent,
    template.documentType,
    template.name,
  ]);

  if (!exampleContent || !insertExampleContent) return null;

  return (
    <div className={styles.templateExample}>
      <img
        className={styles.exampleThumbnail}
        src={template.thumbnail ?? undefined}
      />
      <div className={styles.templateAddButton}>
        <p>See an example?</p>
        <aha-button kind='primary' onClick={insertExample}>
          Add to document
        </aha-button>
      </div>
    </div>
  );
};

type BestPracticesPanelProps = {
  usedTemplateIds: Array<string>;
  insertExampleContent: (content: Object) => void;
};

const BestPracticesPanel: React.FC<BestPracticesPanelProps> = ({
  usedTemplateIds,
  insertExampleContent,
}) => {
  const controller = useController(TemplateController);
  const { templates, loading } = controller.state;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    templates[0]?.id
  );

  const setAndLoadSelectedTemplateId = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);

      // If the selected template doesn't exist in the template controller's
      // data, reload all templates.
      if (
        !controller.state.loadingTemplates &&
        !templates.find(t => t.id === templateId)
      ) {
        void controller.loadTemplates(controller.state.projectId);
      }
    },
    [setSelectedTemplateId, templates, controller]
  );

  // Select the last used template (most recently added) when the available templates are loaded
  useEffect(() => {
    if (usedTemplateIds?.length > 0) {
      setAndLoadSelectedTemplateId(usedTemplateIds[usedTemplateIds.length - 1]);

      if (usedTemplateIds.length === 1) {
        const templateId = getLocalStoragePreference(
          TEMPLATE_LOCAL_STORAGE_KEY
        );
        const keepPromotedTemplateId = getLocalStoragePreference(
          'keepPromotedTemplateId'
        );
        if (templateId === usedTemplateIds[0] && !keepPromotedTemplateId) {
          removeLocalStoragePreference(TEMPLATE_LOCAL_STORAGE_KEY);
        }
      }
    }
  }, [usedTemplateIds, setAndLoadSelectedTemplateId]);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
  const selectedIndex = usedTemplateIds?.findIndex(
    templateId => templateId === selectedTemplateId
  );

  useEffect(() => {
    if (!loading && selectedTemplate) {
      AhaAnalytics.track(
        'Feature-7249764725472497103',
        'User opens best practices tab',
        {
          'Template name': selectedTemplate.name,
          'Template type': humanize(selectedTemplate.documentType),
        }
      );
    }
    // Only want to re-run this (and fire the analytics) when the loading state
    // changes, not whenever the selected template changes.
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className={styles.bestPracticesLoading}>
        <aha-spinner />
      </div>
    );
  }

  if (!selectedTemplate) return null;

  return (
    <div className={styles.bestPracticesPanel}>
      <div className={styles.bestPracticesContent}>
        {usedTemplateIds.length > 1 && (
          <div className={styles.templateCounter}>
            <i className='far fa-table-layout' /> {selectedIndex + 1} /{' '}
            {usedTemplateIds.length}
          </div>
        )}
        <h2 className={styles.heading}>{selectedTemplate.name}</h2>
        <div
          key={selectedTemplateId}
          className={styles.bestPracticeHTML}
          // Use the ref callback to set the target attribute on all links so
          // they open in new tabs.
          ref={el =>
            el
              ?.querySelectorAll(':scope a')
              .forEach(link => link.setAttribute('target', '_blank'))
          }
          // variable name indicates HTML; setter is responsbile for safety
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: selectedTemplate.guideDescription!.htmlBody,
          }}
        />
        <TemplateExampleButton
          template={selectedTemplate}
          insertExampleContent={insertExampleContent}
        />
        {usedTemplateIds.length > 1 && (
          <Tabber
            options={usedTemplateIds}
            selected={selectedTemplateId}
            onSelect={setSelectedTemplateId}
          />
        )}
      </div>
    </div>
  );
};

const ControlledBestPracticesPanel = StartControllerScope(
  TemplateController,
  ApplicationView(BestPracticesPanel)
);

export const BestPracticesSidebarItem = (
  usedTemplateIds: Array<string>,
  templateType: TemplateControllerProps['templateType'],
  insertExampleContent: (content: Object) => void,
  projectId?: RecordId
): SidePanelItem => ({
  id: BEST_PRACTICES_PANEL_KEY,
  name: 'Best practices',
  icon: 'fa-regular fa-lightbulb',
  alwaysRendered: false,
  content: (
    <ControlledBestPracticesPanel
      templateType={templateType}
      insertExampleContent={insertExampleContent}
      includeExampleContent
      usedTemplateIds={usedTemplateIds}
      projectId={projectId}
    />
  ),
});

export default ControlledBestPracticesPanel;
